import { useState, useCallback, useRef, useContext, useEffect } from "react"
import { useDatabase } from "@nozbe/watermelondb/hooks"
import { Q } from "@nozbe/watermelondb"
import type Attachment from "../../model/Attachment"
import type Upload from "../../model/Upload"
import { TableName } from "../../model/schema"
import useObservable from "../useObservable"
import type { BlockBlobClient } from "@azure/storage-blob"
import { BlobServiceClient } from "@azure/storage-blob"
import { storageService } from "../../services/storage.service"
import SettingsContext from "../../contexts/settings.context"
import { deleteFileData, getAllFiles, getFileData, saveFileData } from "../../utils/fileCache.util"
import { updateWatermelonDBObject } from "../../utils/data.util"
import { resizeImage } from "../../utils/image.util"
import useNotification from "../useNotification"
import { BLOB_CHUNK_SIZE } from "../../constants/commonStrings.constant"
import { datadogRum } from "@datadog/browser-rum"
import { useLocalStorage } from "usehooks-ts"
import { IUser } from "../../interfaces"

interface UploadState {
  blobName: string
  fileName: string
  fileSize: number
  uploadedBlocks: string[]
  createdAt: number
  updatedAt: number
}

export interface UploadStateInfo {
  states: UploadState[]
  totalCount: number
  pendingCount: number
  completedCount: number
  totalSize: number
  uploadedSize: number
}

function dataURLtoBlob(dataurl: string) {
  const arr = dataurl.split(",")
  const mime = arr[0].match(/:(.*?);/)[1]
  const bstr = atob(arr[1])
  let n = bstr.length
  const u8arr = new Uint8Array(n)
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n)
  }
  return new Blob([u8arr], { type: mime })
}

// Function to extract expiry date from SAS token
function getSasTokenExpiry(sasToken: string) {
  if (sasToken.startsWith("?")) {
    sasToken = sasToken.substring(1)
  }

  const params = new URLSearchParams(sasToken)
  const se = params.get("se")

  if (se) {
    const decodedSe = decodeURIComponent(se)
    const expiryDate = new Date(decodedSe)
    return expiryDate
  }

  return null
}

export function useAttachments() {
  const database = useDatabase()
  const [error, setError] = useState<any | undefined>()
  const blobServiceClient = useRef<BlobServiceClient | null>(null)
  const { settingsState, setSasToken, updateIDBUsage } = useContext(SettingsContext)
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const [uploadStates, setUploadStates] = useState<UploadState[]>([])
  const [isInitialized, setIsInitialized] = useState(false)
  const { showNotification, NotificationComponent } = useNotification()
  const [user] = useLocalStorage<IUser | null>("user", null)

  useEffect(() => {
    const initializeUploadStates = () => {
      const savedStates = localStorage.getItem("uploads")
      if (savedStates) {
        setUploadStates(JSON.parse(savedStates))
      }
      setIsInitialized(true)
    }

    initializeUploadStates()
  }, [])

  useEffect(() => {
    // Save upload states to localStorage whenever they change
    if (isInitialized) {
      try {
        localStorage.setItem("uploads", JSON.stringify(uploadStates))
      } catch (error) {
        datadogRum.addAction("🛑 unable_to_update_uploads_state_in_local_storage 🛑", {
          error,
          user: user?.email,
          action: `unable to update "uploads" state in local storage`,
          failedUpdatedData: uploadStates,
        })
      }
    }
  }, [uploadStates, isInitialized])

  const initializeBlobServiceClient = useCallback(async () => {
    if (!blobServiceClient.current) {
      let sasToken = settingsState.sasToken
      let sasTokenExpiry = settingsState.sasTokenExpiry ? new Date(settingsState.sasTokenExpiry) : new Date()

      if (!sasToken || !sasTokenExpiry || new Date() >= sasTokenExpiry) {
        try {
          sasToken = await storageService.getAzureSAS("", true)
          console.log("Retrieved SAS token:", sasToken)
          sasTokenExpiry = getSasTokenExpiry(sasToken) || new Date()
          setSasToken(sasToken, sasTokenExpiry) // Update SAS token in settings
        } catch (err) {
          console.warn("Failed to retrieve SAS token. Device might be offline.", err)
          return // Exit if we can't get a SAS token
        }
      }

      const client = new BlobServiceClient(
        `https://${process.env.REACT_APP_AZURE_STORAGE_ACCOUNT}.blob.core.windows.net?${sasToken}`,
      )
      blobServiceClient.current = client
    }
  }, [settingsState.sasToken, setSasToken])

  async function uploadFileInBlocks(
    blockBlobClient: BlockBlobClient,
    fileBlob: Blob,
    blobName: string,
    attachmentData: Attachment,
  ) {
    const blockSize = BLOB_CHUNK_SIZE
    const totalSize = fileBlob.size
    const totalBlocks = Math.ceil(totalSize / blockSize)

    // Find existing upload state or create a new one
    let uploadState = uploadStates.find((state) => state.blobName === blobName)
    if (!uploadState) {
      uploadState = {
        blobName,
        fileName: attachmentData.name ?? "Uploaded Image",
        fileSize: totalSize,
        uploadedBlocks: [],
        createdAt: Date.now(),
        updatedAt: Date.now(),
      }
      setUploadStates((prevStates) => [...prevStates, uploadState!])
    }

    // Save the file data to IndexedDB if not already saved
    const cachedFileBlob = await getFileData(blobName)
    if (!cachedFileBlob) {
      try {
        await saveFileData(blobName, fileBlob)

        updateIDBUsage()
      } catch (error) {
        console.error(`Failed to save file data for ${blobName}:`, error)

        datadogRum.addAction("🛑 failed_to_upload_data 🛑", {
          error,
          user: user?.email,
          action: `Failed to save file data for ${blobName}`,
          blockBlobClient,
          fileBlob,
          blobName,
          attachmentData,
        })

        throw error
      }
    } else {
      fileBlob = cachedFileBlob
    }

    const blockIds = []

    for (let i = 0; i < totalBlocks; i++) {
      const blockId = btoa(`block-${i.toString().padStart(6, "0")}`)
      blockIds.push(blockId)

      // Skip already uploaded blocks
      if (uploadState.uploadedBlocks.includes(blockId)) {
        continue
      }

      const start = i * blockSize
      const end = Math.min(start + blockSize, totalSize)
      const blockData = fileBlob.slice(start, end)

      try {
        // Upload the block
        await blockBlobClient.stageBlock(blockId, blockData, end - start)

        // Update the upload state
        uploadState.uploadedBlocks.push(blockId)
        uploadState.updatedAt = Date.now()
        setUploadStates((prevStates) => prevStates.map((state) => (state.blobName === blobName ? uploadState! : state)))

        console.log(`Uploaded block ${i + 1}/${totalBlocks}`)
      } catch (error) {
        console.error(`Error uploading block ${i + 1}:`, error)
        throw error
      }
    }

    // Commit the blocks
    await blockBlobClient.commitBlockList(blockIds)

    // Set metadata
    const metadata = {
      FileName: attachmentData.name ?? "Uploaded Image",
      ContentType: fileBlob.type,
      // Add any other metadata you want to store
    }
    await blockBlobClient.setMetadata(metadata)

    // Set HTTP headers
    const blobHttpHeaders = {
      blobContentType: fileBlob.type,
      // blobContentDisposition: `attachment; filename="${attachmentData.name ?? "Uploaded Image"}"`,
    }
    await blockBlobClient.setHTTPHeaders(blobHttpHeaders)

    // Remove the upload state
    setUploadStates((prevStates) => prevStates.filter((state) => state.blobName !== blobName))
    // Remove the file data from IndexedDB
    try {
      await deleteFileData(blobName)
    } catch (error) {
      console.error(`Failed to delete file data for ${blobName}:`, error)
    }

    updateIDBUsage()

    console.log("Upload complete")

    datadogRum.addAction("✅ image_uploaded_successfully", {
      user: user?.email,
      action: `uploaded ${attachmentData?.name}`,
      blockBlobClient,
      fileBlob,
      blobName,
      attachmentData,
    })
  }

  const resumeIncompleteUploads = useCallback(async () => {
    if (!isOnline || !isInitialized) {
      console.log("Device is offline or not initialized. Cannot resume uploads.")
      return
    }

    try {
      await initializeBlobServiceClient()
    } catch (err) {
      console.warn("Device is offline or failed to initialize blobServiceClient:", err)
    }

    for (let i = 0; i < uploadStates.length; i++) {
      const uploadState = uploadStates[i]
      console.log(`Processing upload ${i + 1}/${uploadStates.length}, Blob: ${uploadState.blobName}`)
      try {
        const blobName = uploadState.blobName
        const containerClient = blobServiceClient.current?.getContainerClient(
          process.env.REACT_APP_AZURE_STORAGE_CONTAINER ?? "",
        )
        const blockBlobClient = containerClient?.getBlockBlobClient(blobName)

        // Retrieve the file data from IndexedDB
        const fileBlob = await getFileData(blobName)

        if (!fileBlob) {
          console.error(`File data not found for ${blobName}`)
          // Cannot resume upload without file data; remove upload record
          setUploadStates((prevStates) => prevStates.filter((state) => state.blobName !== blobName))
          continue
        }

        // Resume uploading the remaining blocks
        if (!blockBlobClient) {
          throw new Error("Block blob client not found")
        }

        await uploadFileInBlocks(blockBlobClient, fileBlob, uploadState.blobName, {
          name: uploadState.fileName,
        } as Attachment)
        console.log(`Successfully resumed and completed upload for ${blobName}`)

        datadogRum.addAction("✅ uploaded_file_to_blob_storage_successfully", {
          user: user?.email,
          action: `successfully uploaded blobname: ${blobName}`,
          blockBlobClient,
          fileBlob,
          blobName,
        })
      } catch (error) {
        showNotification({
          message: `Error resuming upload for ${uploadState.blobName}`,
          severity: "error",
        })
        console.error(`Error resuming upload for ${uploadState.blobName}:`, error)
      }
    }
  }, [uploadStates, blobServiceClient, initializeBlobServiceClient, isOnline, isInitialized])

  // Effect to handle online/offline status
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true)
    }

    const handleOffline = () => {
      setIsOnline(false)
    }

    window.addEventListener("online", handleOnline)
    window.addEventListener("offline", handleOffline)

    return () => {
      window.removeEventListener("online", handleOnline)
      window.removeEventListener("offline", handleOffline)
    }
  }, [])

  // New effect to handle resuming uploads when initialized and online
  useEffect(() => {
    if (isInitialized && isOnline) {
      console.log("Initialized and online, resuming uploads")
      resumeIncompleteUploads()
    }
  }, [isInitialized, isOnline])

  const addAttachment = useCallback(
    async (newAttachmentData: Attachment) => {
      try {
        await initializeBlobServiceClient()
      } catch (err) {
        console.warn("Device is offline or failed to initialize blobServiceClient:", err)
      }

      try {
        // Always store the attachment metadata in WatermelonDB
        const { data, ...attachmentDataWithoutData } = newAttachmentData

        if (!data) {
          throw new Error("No data found in the attachment")
        }

        // Generate thumbnail
        const thumbnailDataUrl = await resizeImage(data, 300, 300)

        const fileBlob = dataURLtoBlob(data)
        const thumbnailBlob = dataURLtoBlob(thumbnailDataUrl)
        const totalSize = fileBlob.size

        const blobName = `Documents/${newAttachmentData.path}/${newAttachmentData.name}`
        const thumbnailBlobName = `Documents/${newAttachmentData.path}/${newAttachmentData.name}_thumbnail.jpg`

        await database.write(async () => {
          await database.collections.get<Attachment>(TableName.Attachment).create((attachment) => {
            updateWatermelonDBObject(attachment, attachmentDataWithoutData)
          })
        })

        // Create upload states for main file and thumbnail
        const newUploadStates: UploadState[] = [
          {
            blobName,
            fileName: attachmentDataWithoutData.name ?? "Uploaded Image",
            fileSize: totalSize,
            uploadedBlocks: [],
            createdAt: Date.now(),
            updatedAt: Date.now(),
          },
          {
            blobName: thumbnailBlobName,
            fileName: `${attachmentDataWithoutData.name}_thumbnail.jpg`,
            fileSize: thumbnailBlob.size,
            uploadedBlocks: [],
            createdAt: Date.now(),
            updatedAt: Date.now(),
          },
        ]
        setUploadStates((prevStates) => [...prevStates, ...newUploadStates])

        // Save file data to IndexedDB using the attachment ID
        await saveFileData(blobName, fileBlob)
        await saveFileData(thumbnailBlobName, thumbnailBlob)

        updateIDBUsage()
        // If online and blobServiceClient is initialized, attempt to upload immediately
        if (isOnline && blobServiceClient.current) {
          await resumeIncompleteUploads()
        } else {
          console.warn("Attachment saved for later upload when online.")
        }

        return newAttachmentData
      } catch (err) {
        setError(err)
        console.error("Error saving attachment:", err)
      }
    },
    [database, initializeBlobServiceClient, isOnline, isInitialized],
  )

  const addAttachmentToBatch = useCallback(
    async (newAttachmentData: Attachment) => {
      try {
        // Always store the attachment metadata in WatermelonDB
        const { data, ...attachmentDataWithoutData } = newAttachmentData

        if (!data) {
          throw new Error("No data found in the attachment")
        }

        // Generate thumbnail
        const thumbnailDataUrl = await resizeImage(data, 300, 300)

        const fileBlob = dataURLtoBlob(data)
        const thumbnailBlob = dataURLtoBlob(thumbnailDataUrl)
        const totalSize = fileBlob.size

        const blobName = `Documents/${newAttachmentData.path}/${newAttachmentData.name}`
        const thumbnailBlobName = `Documents/${newAttachmentData.path}/${newAttachmentData.name}_thumbnail.jpg`

        await database.write(async () => {
          await database.collections.get<Attachment>(TableName.Attachment).create((attachment) => {
            updateWatermelonDBObject(attachment, attachmentDataWithoutData)
          })
        })

        // Create upload states for main file and thumbnail
        const newUploadStates: UploadState[] = [
          {
            blobName,
            fileName: attachmentDataWithoutData.name ?? "Uploaded Image",
            fileSize: totalSize,
            uploadedBlocks: [],
            createdAt: Date.now(),
            updatedAt: Date.now(),
          },
          {
            blobName: thumbnailBlobName,
            fileName: `${attachmentDataWithoutData.name}_thumbnail.jpg`,
            fileSize: thumbnailBlob.size,
            uploadedBlocks: [],
            createdAt: Date.now(),
            updatedAt: Date.now(),
          },
        ]
        setUploadStates((prevStates) => [...prevStates, ...newUploadStates])

        // Save file data to IndexedDB using the attachment ID
        await saveFileData(blobName, fileBlob)
        await saveFileData(thumbnailBlobName, thumbnailBlob)

        updateIDBUsage()

        return newAttachmentData
      } catch (err) {
        setError(err)
        console.error("Error adding attachment to batch:", err)
      }
    },
    [updateIDBUsage],
  )

  const processBatchUpload = useCallback(async () => {
    try {
      await initializeBlobServiceClient()
    } catch (err) {
      console.warn("Device is offline or failed to initialize blobServiceClient:", err)
      return
    }

    // Attempt to upload if online
    if (isOnline && blobServiceClient.current) {
      await resumeIncompleteUploads()
    } else {
      console.warn("Batch saved for later upload when online.")
    }
  }, [initializeBlobServiceClient, isOnline])

  // Function to update an existing attachment
  const updateAttachment = useCallback(
    async (updatedAttachmentData: Attachment) => {
      try {
        await database.write(async () => {
          const attachment = await database.collections.get(TableName.Attachment).find(updatedAttachmentData.id)
          await attachment.update((attachment) => {
            Object.assign(attachment, updatedAttachmentData)
          })
        })
      } catch (err) {
        setError(err)
        console.error("Error updating attachment:", err)
      }
    },
    [database],
  )

  // Function to delete an attachment via watermelonDB and syncProvider
  const deleteAttachment = useCallback(
    async (id: string): Promise<string | undefined> => {
      try {
        await database.write(async () => {
          const attachment = await database.collections.get(TableName.Attachment).find(id)
          await attachment.markAsDeleted()
        })
        return "Deleted Successfully"
      } catch (err) {
        setError(err)
        console.error("Error deleting attachment:", err)
        datadogRum.addAction("🛑 error_deleting_attachment_via_watermelon_db 🛑", {
          user: user?.email,
          action: `unable to delete attachment through watermelonDB. attachmentId:  ${id}`,
          err,
          id,
        })
        throw err
      }
    },
    [database],
  )

  // Add this new method within the useAttachments hook
  const reconcileOrphanedUploads = useCallback(async () => {
    try {
      // Get all files from IndexedDB
      const files = await getAllFiles()
      console.log(`Found ${files.length} files in IndexedDB`)

      // Get all attachment records from WatermelonDB
      const attachments = await database.collections.get<Attachment>(TableName.Attachment).query().fetch()

      // Create lookup maps
      const existingBlobPaths = new Map(attachments.map((att) => [`Documents/${att.path}/${att.name}`, att]))

      const existingUploadStates = new Set(uploadStates.map((state) => state.blobName))

      // Keep track of new upload states
      const newUploadStates: UploadState[] = []

      for (const { key, blob } of files) {
        const isThumbnail = key.toString().endsWith("_thumbnail.jpg")
        if (isThumbnail) continue // Skip thumbnails as they'll be handled with their main image

        const needsWatermelonDBRecord = !existingBlobPaths.has(key.toString())
        const needsUploadState = !existingUploadStates.has(key.toString())

        // Parse the blob path to extract relevant information
        const pathMatch = key.toString().match(/^Documents\/(.+)\/([^/]+)$/)
        if (!pathMatch) {
          console.warn(`Invalid blob path format: ${key}`)
          continue
        }

        const [, path, name] = pathMatch

        try {
          // Handle missing WatermelonDB record
          if (needsWatermelonDBRecord) {
            // Create thumbnail
            const fileReader = new FileReader()
            const dataUrl = await new Promise<string>((resolve) => {
              fileReader.onload = () => resolve(fileReader.result as string)
              fileReader.readAsDataURL(blob)
            })

            const thumbnailDataUrl = await resizeImage(dataUrl, 300, 300)
            const thumbnailBlob = dataURLtoBlob(thumbnailDataUrl)
            const thumbnailBlobName = `${key}_thumbnail.jpg`

            // Create new attachment record in WatermelonDB
            await database.write(async () => {
              await database.collections.get<Attachment>(TableName.Attachment).create((attachment) => {
                updateWatermelonDBObject(attachment, {
                  name,
                  path,
                  contentType: blob.type,
                  size: blob.size,
                  createdAt: new Date().toISOString(),
                  updatedAt: new Date().toISOString(),
                  status: "pending",
                })
              })
            })

            // Save thumbnail to IndexedDB if it doesn't exist
            const thumbnailExists = await getFileData(thumbnailBlobName)
            if (!thumbnailExists) {
              await saveFileData(thumbnailBlobName, thumbnailBlob)
            }

            console.log(`Created WatermelonDB record for: ${key}`)
          }

          // Handle missing upload state (regardless of WatermelonDB status)
          if (needsUploadState) {
            // Get or create thumbnail blob
            let thumbnailBlob: Blob
            const thumbnailBlobName = `${key}_thumbnail.jpg`
            const existingThumbnail = await getFileData(thumbnailBlobName)

            if (existingThumbnail) {
              thumbnailBlob = existingThumbnail
            } else {
              const fileReader = new FileReader()
              const dataUrl = await new Promise<string>((resolve) => {
                fileReader.onload = () => resolve(fileReader.result as string)
                fileReader.readAsDataURL(blob)
              })
              const thumbnailDataUrl = await resizeImage(dataUrl, 300, 300)
              thumbnailBlob = dataURLtoBlob(thumbnailDataUrl)
              await saveFileData(thumbnailBlobName, thumbnailBlob)
            }

            // Create upload states for both main file and thumbnail
            newUploadStates.push(
              {
                blobName: key.toString(),
                fileName: name,
                fileSize: blob.size,
                uploadedBlocks: [],
                createdAt: Date.now(),
                updatedAt: Date.now(),
              },
              {
                blobName: thumbnailBlobName,
                fileName: `${name}_thumbnail.jpg`,
                fileSize: thumbnailBlob.size,
                uploadedBlocks: [],
                createdAt: Date.now(),
                updatedAt: Date.now(),
              },
            )

            console.log(`Created upload state for: ${key}`)
          }
        } catch (error) {
          console.error(`Error reconciling upload for ${key}:`, error)
          showNotification({
            message: `Failed to reconcile upload: ${key}`,
            severity: "error",
          })
        }
      }

      // Update upload states if we found any files needing upload
      if (newUploadStates.length > 0) {
        setUploadStates((prevStates) => [...prevStates, ...newUploadStates])
        showNotification({
          message: `Added ${newUploadStates.length / 2} files to upload queue`,
          severity: "success",
        })

        // Attempt to upload if we're online
        if (isOnline && blobServiceClient.current) {
          await resumeIncompleteUploads()
        }
      }

      return {
        newUploadsCount: newUploadStates.length / 2,
        totalFiles: files.length,
      }
    } catch (error) {
      console.error("Error reconciling orphaned uploads:", error)
      showNotification({
        message: "Failed to reconcile orphaned uploads",
        severity: "error",
      })
      throw error
    }
  }, [database, isOnline, resumeIncompleteUploads, showNotification, uploadStates])

  const getUploadStates = useCallback((): UploadStateInfo => {
    const nonThumbnailStates = uploadStates.filter((state) => !state.blobName.endsWith("_thumbnail.jpg"))

    const totalSize = uploadStates.reduce((sum, state) => sum + state.fileSize, 0)
    const uploadedSize = uploadStates.reduce((sum, state) => {
      const blockSize = BLOB_CHUNK_SIZE
      return sum + state.uploadedBlocks.length * blockSize
    }, 0)

    return {
      states: uploadStates,
      totalCount: nonThumbnailStates.length,
      pendingCount: nonThumbnailStates.filter((state) => state.uploadedBlocks.length === 0).length,
      completedCount: nonThumbnailStates.filter((state) => {
        const totalBlocks = Math.ceil(state.fileSize / BLOB_CHUNK_SIZE)
        return state.uploadedBlocks.length === totalBlocks
      }).length,
      totalSize,
      uploadedSize,
    }
  }, [uploadStates])

  return {
    addAttachment,
    updateAttachment,
    deleteAttachment,
    resumeIncompleteUploads,
    addAttachmentToBatch,
    processBatchUpload,
    reconcileOrphanedUploads,
    getUploadStates,
    error,
    isOnline,
  }
}
